import styled, { css } from 'styled-components';
import { Typography } from '../../../global/theme';
import { mobile_xl } from '../../../global/mediaQueryMixin';
import { AlignCenter, FlexBetween, HideScrollbar, LineClamp } from '../../../global/commonStyle';

interface DroppableContainerProps {
  isMobile: boolean;
}

const DroppableContainer = styled.div<DroppableContainerProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? 'var(--space-4)' : 'var(--space-2)')};
`;

const SortableItemWrapper = styled.div<{
  isDragging?: boolean;
  isOverlay?: boolean;
  cssTransform?: string;
  cssTransition?: string;
}>`
  z-index: ${({ isOverlay, isDragging }) =>
    isOverlay ? 'var(--z-index-modal)' : isDragging ? 'var(--z-index-base)' : ''};
  transform: ${({ cssTransform }) => cssTransform || 'none'};
  transition: ${({ cssTransition }) => cssTransition || 'none'};
  width: 100%;
  pointer-events: ${({ isOverlay }) => (isOverlay ? 'none' : 'auto')};
  opacity: ${({ isDragging, isOverlay }) => (isDragging && !isOverlay ? 0.4 : 1)};
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 306px;
  ${mobile_xl(css`
    ${HideScrollbar}
    max-width: 100%;
    height: 100dvh;
    background-color: var(--background-secondary);
  `)}
`;

const MessageHeader = styled.div`
  ${FlexBetween}
  padding: var(--space-11) var(--space-14) var(--space-11) var(--space-16);
  border-bottom: 0.5px solid var(--border-primary);
  background-color: var(--background-navbar);
  position: sticky;
  top: 0;
`;

const LeftHeader = styled.div`
  ${Typography.heading_md}
  color: var(--text-primary);
`;

const MessageData = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--space-12);
  gap: var(--space-20);
  ${mobile_xl(css`
    padding: var(--space-8) var(--space-16) var(--space-96);
    gap: var(--space-24);
  `)};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-8);
  ${mobile_xl(css`
    gap: var(--space-4);
  `)};
`;

const Box = styled.div<{ isActive: boolean; forceHover?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: var(--space-5);
  border-radius: var(--border-radius-8);
  &:hover {
    background-color: var(--background-hover-navbar);
    cursor: pointer;
  }
  ${(props: { isActive: boolean; forceHover?: boolean }) =>
    props.isActive &&
    css`
      background-color: var(--background-active-navbar);
      &:hover {
        background-color: var(--background-active-navbar);
      }
      ${mobile_xl(css`
        background-color: unset;
      `)}
    `}
  ${mobile_xl(css`
    padding: var(--space-8) var(--space-4);
    background-color: unset;
    &:hover {
      background-color: var(--neutral-secondary);
    }
  `)}
  ${(props: { forceHover?: boolean }) =>
    props.forceHover &&
    css`
      background-color: var(--background-active-navbar);
      ${mobile_xl(css`
        background-color: var(--neutral-secondary);
      `)}
    `}
`;
const Icon = styled.div`
  display: flex;
`;
const MessageDetail = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  align-items: flex-start;
`;
const Name = styled.div<{ hasUnreadMessages?: boolean }>`
  ${Typography.body_md}
  color: var(--text-primary);
  ${LineClamp(1)}
  ${(props: { hasUnreadMessages?: boolean }) =>
    props.hasUnreadMessages &&
    css`
      ${Typography.body_md_semibold}
    `}
  ${mobile_xl(css`
    line-height: 20px;
  `)}
`;

const NewChat = styled.div`
  ${AlignCenter}
  gap: var(--space-12);
  padding: var(--space-5);
  background-color: var(--background-active-navbar);
  border-radius: var(--border-radius-8);
`;

const RightChat = styled.div`
  ${AlignCenter}
  gap: var(--space-11);
`;

const SectionHeading = styled.p`
  ${Typography.body_md_medium};
  margin: var(--space-0);
  color: var(--text-secondary);
  ${mobile_xl(css`
    padding-inline: var(--space-4);
  `)}
`;

const UnpinChat = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  ${mobile_xl(css`
    gap: var(--space-4);
  `)}
`;

export {
  DroppableContainer,
  SortableItemWrapper,
  MessageHeader,
  MessageContent,
  LeftHeader,
  MessageData,
  Box,
  MessageDetail,
  Name,
  Icon,
  NewChat,
  RightChat,
  SectionHeading,
  UnpinChat,
  Section
};
